import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Params, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { RoutingService } from '@spartacus/core';
import { Observable } from 'rxjs';
import { map, switchMap, withLatestFrom } from 'rxjs/operators';
import { CatalogKeysService, CatalogRouterStateService, ProductService } from '../../../core/catalog';
import { GlobalMessageService, GlobalMessageType } from '../../../core/global-message';
import { EntityMap, PrincipalConfiguration, Product } from '../../../core/model';
import { PrincipalConfigurationService } from '../../../core/user';
import { prepareUrlForLink, sanitizeArticleCode } from '../../../core/util';
import { DocumentsService } from '../../documents-base';

@Injectable({ providedIn: 'root' })
export class PdpGuard {
  constructor(
    private productService: ProductService,
    private router: Router,
    private catalogKeysService: CatalogKeysService,
    private globalMessageService: GlobalMessageService,
    private documentsService: DocumentsService,
    private principalConfigurationService: PrincipalConfigurationService,
    private routingService: RoutingService,
    private catalogRouterStateService: CatalogRouterStateService
  ) {}

  canActivate(route: ActivatedRouteSnapshot, routerState: RouterStateSnapshot): Observable<boolean | UrlTree> {
    const productCode = this.catalogRouterStateService.getProductCode(route.params, route.queryParams);
    return this.catalogKeysService.getCatalogUsageKeyForParams(route.queryParams['mya']).pipe(
      switchMap((catalogUsageKey) => this.productService.getProduct(productCode, catalogUsageKey)),
      withLatestFrom(this.principalConfigurationService.getValue('defaultPdpArticleSortingValue')),
      map(([product, sorting]) => {
        if (!product) {
          this.handleNoProductFound(route.params, route.queryParams);
          return false;
        }

        // Not sure why we need to load the product documents here, but I guess it's good! Maybe we can refactor this...
        this.documentsService.loadProductDocuments(product.code, true);

        // We should only return a URL if we have changed the query parameters, or else we will return true.
        const queryParams = this.prepareQueryParams(route.queryParams, product, sorting);
        if (!!queryParams) {
          const url = prepareUrlForLink(routerState.url, queryParams);
          return this.router.parseUrl(url);
        }

        return true;
      })
    );
  }

  private prepareQueryParams(queryParams: Params, product: Product, sorting: PrincipalConfiguration): EntityMap<string> {
    let value: EntityMap<string>;
    const articleCode = sanitizeArticleCode(queryParams['a']);

    // Set default sorting if provided
    if (typeof sorting?.value === 'string' && !queryParams['sort'] && sorting.value.length > 0) {
      value = {
        sort: sorting.value,
      };
    }

    // Remove &a=articleCode and &ks=*** if the article code is not included in the product articles array.
    if (articleCode && !product.articles.includes(articleCode)) {
      value = {
        ...value,
        ks: null,
        a: null,
      };
    }

    // If the product only has one article, we should add "&a=articleCode" to the URL
    if (product?.articles?.length === 1 && articleCode !== product?.articles[0]) {
      value = {
        ...value,
        a: product?.articles[0],
      };
    }
    return value;
  }

  private handleNoProductFound(params: Params, routeQueryParams: Params): void {
    this.globalMessageService.add(
      {
        key: 'product.productNotFound_message',
      },
      GlobalMessageType.MSG_TYPE_ERROR
    );

    let queryParams = null;
    const categoryCode = this.catalogRouterStateService.getLeafCategoryCode(params, routeQueryParams);

    if (!!routeQueryParams['aou']) {
      queryParams = { aou: routeQueryParams['aou'] };
    } else if (!!categoryCode) {
      queryParams = { query: `::allCategories:${categoryCode}` };
    }

    this.routingService.go({ cxRoute: 'catalog' }, { queryParams });
  }
}
